import { currentSchoolYearLabel } from "@/lib/school-year";
import type { Locale } from "@/lib/i18n";

export type ComparisonColumn = "swedish" | "international" | "spanish";

export type ComparisonRow = {
  label: string;
  swedish: string;
  international: string;
  spanish: string;
};

export type ComparisonSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type ComparisonFaq = { q: string; a: string };

export type ComparisonContent = {
  eyebrow: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  neutralityNote: string;
  tableHeading: string;
  tableIntro: string;
  columnLabels: { row: string; swedish: string; international: string; spanish: string };
  rows: ComparisonRow[];
  tableFootnote: string;
  sections: ComparisonSection[];
  checklistHeading: string;
  checklistIntro: string;
  checklist: string[];
  ownPerspectiveHeading: string;
  ownPerspectiveNote: string;
  ownPerspective: string[];
  faqHeading: string;
  faq: ComparisonFaq[];
  ctaTitle: string;
  ctaText: string;
  ctaLabel: string;
  ctaTo: string;
  links: { to: string; label: string }[];
};

const sv: ComparisonContent = {
  eyebrow: "Guide",
  title: "Svensk, internationell eller spansk skola i Spanien?",
  metaTitle: "Svensk, internationell eller spansk skola? | Jämförelse",
  metaDescription:
    "Neutral jämförelse av svensk, internationell och spansk skola på Costa del Sol: läroplan, språk, återflytt till Sverige, integration, betyg och kostnad.",
  intro:
    "Ska barnen gå i svensk, internationell eller spansk skola när ni bor i Spanien? Det finns inget svar som är rätt för alla. Den här guiden jämför de tre skolformerna sakligt så att ni kan väga in hur länge ni ska stanna, vad som händer efteråt och vad familjen vill få ut av åren här.",
  neutralityNote:
    "Vi driver en svensk skola, men den här jämförelsen är skriven för att vara användbar även om ni väljer något annat. Inget alternativ är generellt bäst – och skolor inom samma kategori skiljer sig åt, så besök gärna flera innan ni bestämmer er.",
  tableHeading: "Jämförelse i korthet",
  tableIntro:
    "Tabellen beskriver hur skolformerna brukar fungera i Málagaprovinsen. Enskilda skolor kan avvika på alla punkter.",
  columnLabels: {
    row: "",
    swedish: "Svensk skola",
    international: "Internationell skola",
    spanish: "Spansk skola",
  },
  rows: [
    {
      label: "Läroplan",
      swedish: "Svensk läroplan och svenska kursplaner",
      international: "Brittisk, amerikansk eller IB – ibland flera parallellt",
      spanish: "Spansk läroplan enligt LOMLOE",
    },
    {
      label: "Undervisningsspråk",
      swedish: "Svenska, med spanska och engelska som ämnen",
      international: "Engelska, med spanska som ämne",
      spanish: "Spanska (i Andalusien ofta med engelsk profil)",
    },
    {
      label: "Vid återflytt till Sverige",
      swedish: "Eleven följer samma kursplan hela tiden",
      international: "Viss anpassning, särskilt i svenska och SO",
      spanish: "Större anpassning i svenska, SO och ofta matematikens upplägg",
    },
    {
      label: "Lokal integration",
      swedish: "Medel till hög – spanska varje dag, svenskt sammanhang i skolan",
      international: "Varierar – ofta internationell kompiskrets snarare än lokal",
      spanish: "Hög – vardagen sker helt på spanska",
    },
    {
      label: "Betyg och behörighet",
      swedish: "Svenska omdömen och betyg som fungerar i svenskt system",
      international: "IGCSE, A-levels, IB eller amerikansk high school diploma",
      spanish: "ESO och Bachillerato, med Selectividad till spanskt universitet",
    },
    {
      label: "Kostnadsbild",
      swedish: "Avgiftsfinansierad friskola",
      international: "Ofta de högsta avgifterna i området",
      spanish: "Offentlig skola avgiftsfri, concertada och privada kostar",
    },
    {
      label: "Passar ofta bäst",
      swedish: "Familjer som ska stanna en period och kanske flytta hem igen",
      international: "Familjer som rör sig mellan länder eller siktar på engelskspråkigt universitet",
      spanish: "Familjer som ska stanna långsiktigt och vill ha barnen helt i det spanska systemet",
    },
    {
      label: "Värdegrund och elevsyn",
      swedish: "Skiljer sig mellan skolor – kontrollera hur värderingarna märks i vardagen",
      international: "Skiljer sig mellan skolor – kontrollera hur värderingarna märks i vardagen",
      spanish: "Skiljer sig mellan skolor – kontrollera hur värderingarna märks i vardagen",
    },
  ],
  tableFootnote:
    `Uppgifterna är generella och gäller läsåret ${currentSchoolYearLabel()}. Kontrollera alltid direkt med respektive skola – utbud, språkprofil och avgifter ändras.`,
  sections: [
    {
      heading: "Svensk skola",
      paragraphs: [
        "En svensk skola utomlands följer svensk läroplan, med svenska som undervisningsspråk och svenska kunskapskrav. Det gör övergången tillbaka till en svensk klass förutsägbar: eleven har läst samma centrala innehåll som klasskamraterna hemma.",
        "Baksidan är att den svenska vardagen kan bli en bubbla om skolan inte aktivt arbetar med spanska och lokal kontakt. Fråga hur mycket spanska som faktiskt används under veckan och vad som händer efter årskurs 6 eller 9 på just den skolan.",
      ],
      bullets: [
        "Styrkor: kontinuitet i svenska, tydlig återflytt, svensk skolkultur och lärare som följer varje elev nära.",
        "Att tänka på: fråga hur skolan arbetar med spanska och lokal kontakt, och hur ämnesutbudet ser ut i just din årskurs.",
        "Trygg övergång: en liten skola gör flytten lättare – lärarna känner varje elev från dag ett, undervisningen anpassas snabbt och eleven får mer taltid och stöd varje dag, både vid start och vid en återflytt till Sverige.",

      ],
    },
    {
      heading: "Internationell skola",
      paragraphs: [
        "Internationella skolor undervisar på engelska enligt brittisk, amerikansk eller IB-modell. De passar familjer som rör sig mellan länder, eftersom samma system finns i många städer, och de ger ofta en bred väg vidare till engelskspråkiga universitet.",
        "Skillnaderna mellan skolorna är stora. Två skolor i samma stad kan ha helt olika storlek, examensmodell och andel elever som stannar hela skoltiden. Vid en återflytt till Sverige behöver eleven ofta komplettera i svenska och samhällsorienterande ämnen.",
      ],
      bullets: [
        "Styrkor: engelska på hög nivå, internationellt erkända examina, flyttbarhet mellan länder.",
        "Att tänka på: avgiftsnivån, hur skolan stöttar nya språkinlärare och hur svenskan hålls vid liv.",
      ],
    },
    {
      heading: "Spansk skola",
      paragraphs: [
        "Spansk skola – offentlig, concertada eller privat – ger den snabbaste vägen in i det lokala samhället. Barnen får spanska på modersmålsnivå över tid, får lokala kompisar och följer samma system som grannarna.",
        "Det tar dock tid innan ett barn utan spanska kan följa undervisningen fullt ut, och stödet för nyanlända varierar mellan skolor. Ska familjen flytta hem igen inom några år behöver svenskan hållas vid liv vid sidan av, till exempel genom kompletterande svensk undervisning.",
      ],
      bullets: [
        "Styrkor: språket, integrationen, låg eller ingen avgift i offentlig skola.",
        "Att tänka på: startsträckan i språket, skoldagens upplägg och hur betygen översätts vid en återflytt.",
      ],
    },
    {
      heading: "Så väger ni ihop det",
      paragraphs: [
        "Tre frågor brukar avgöra: Hur länge ska ni stanna? Vad händer efter den perioden? Och hur mycket tid orkar familjen lägga på att stötta ett nytt språk parallellt med skolarbetet?",
        "Ett vanligt mönster är att familjer som planerar en period på ett till fyra år väljer svensk eller internationell skola, medan familjer som ser Spanien som permanent oftare väljer spansk skola. Men det är just ett mönster, inte en regel – barnets ålder, språkvana och personlighet väger tyngre än genomsnittet.",
      ],
    },
    {
      heading: "Granska värdegrunden – inte bara orden",
      paragraphs: [
        "En skolas värdegrund påverkar barnets vardag lika mycket som läroplan, språk och avgift. Fina ord på en webbplats räcker inte. Titta på hur vuxna möter eleverna, hur konflikter följs upp, hur stöd sätts in och om personalen faktiskt känner varje barns styrkor och behov.",
        "Storlek är inte automatiskt ett mått på kvalitet. En stor skola kan ha starka arbetssätt och nära uppföljning, och en liten skola måste också kunna visa att värderingarna styr besluten. Frågan är om skolan främst jagar fler elever eller om tillväxten sker utan att barnets trygghet, utveckling och möjlighet till en bra start i livet tappas bort.",
      ],
    },
  ],
  checklistHeading: "Frågor att ställa på skolbesöket",
  checklistIntro:
    "Samma frågor fungerar oavsett skolform och gör det lättare att jämföra skolor rakt av.",
  checklist: [
    "Hur många elever går i klassen, och hur många vuxna finns med under dagen?",
    "Vilket språk används i klassrummet, på rasterna och i kommunikationen hem?",
    "Hur stöttas ett barn som börjar utan att kunna undervisningsspråket?",
    "Vad händer efter den högsta årskursen skolan erbjuder?",
    "Hur ser skolan på återflytt till hemlandet – finns det tidigare exempel?",
    "Vilka avgifter tillkommer utöver terminsavgiften (mat, utflykter, material, buss)?",
    "Vilken myndighet har tillsyn över skolan, och vilka tillstånd finns?",
    "Hur arbetar skolan med elevhälsa, trygghet och extra stöd?",
    "Vilka värderingar styr skolan, och hur märks de i klassrummet, på rasten och när något går fel?",
    "Hur säkerställer skolan personlig uppföljning när elevantalet växer?",
  ],
  ownPerspectiveHeading: "Vårt eget perspektiv",
  ownPerspectiveNote:
    "Här beskriver vi vår egen skola. Läs det som ett av flera alternativ – inte som en rekommendation mot de andra.",
  ownPerspective: [
    "Vi undervisar på svenska enligt svensk läroplan, med utökad spanska och engelska varje vecka.",
    "Vi strävar efter högst 16 elever per grupp och svensk lärarlegitimation i all svensk undervisning. Gruppstorlek och bemanning kan variera.",
    "Skolan är en privat skola med tillstånd och tillsyn från Junta de Andalucía (den spanska utbildningsmyndigheten i Andalusien), och är godkänd av Skolverket för kompletterande svenska.",
    "Från årskurs 7 studerar eleverna via Sofia Distans och gymnasiet via Hermods, med handledning på plats.",
  ],
  faqHeading: "Vanliga frågor",
  faq: [
    {
      q: "Vad är skillnaden mellan svensk och internationell skola i Spanien?",
      a: "Svensk skola undervisar på svenska enligt svensk läroplan, internationell skola oftast på engelska enligt brittisk, amerikansk eller IB-modell. Skillnaden märks mest i språk och vid en återflytt till Sverige.",
    },
    {
      q: "Kan barnet gå tillbaka till svensk skola efteråt?",
      a: "Ja. Från en svensk skola sker övergången utan kompletteringar eftersom kursplanen är densamma. Från internationell eller spansk skola går det också bra, men svenska och de samhällsorienterande ämnena behöver ofta hämtas ikapp.",
    },
    {
      q: "Måste barnet kunna spanska för att börja i spansk skola?",
      a: "Nej, men det underlättar. Yngre barn kommer oftast in i språket snabbare än tonåringar, och stödet för nyanlända skiljer sig mycket mellan skolor – fråga specifikt hur det ser ut.",
    },
    {
      q: "Vad kostar de olika skolformerna?",
      a: "Offentlig spansk skola är avgiftsfri, concertada har lägre avgifter, medan internationella och svenska skolor är avgiftsfinansierade. Internationella skolor ligger ofta högst i området.",
    },
    {
      q: "Vilket alternativ är bäst?",
      a: "Det beror på hur länge ni ska stanna och vad som händer sedan. Inget av alternativen är generellt bättre än de andra – jämför enskilda skolor, inte bara skolformer.",
    },
  ],
  ctaTitle: "Vill ni se hur en svensk skoldag ser ut här?",
  ctaText:
    "Läs mer om skolan eller skicka in en ansökan så återkommer vi med besked om plats och startdatum.",
  ctaLabel: "Till ansökan",
  ctaTo: "/ansokan",
  links: [
    { to: "/om-skolan", label: "Om skolan" },
    { to: "/fragor-och-svar", label: "Frågor och svar" },
    { to: "/avgifter", label: "Avgifter" },
    { to: "/flytta-till-spanien-med-barn", label: "Flytta till Spanien med barn" },
    { to: "/skola-i-marbella-och-estepona", label: "Skola i Marbella och Estepona" },
  ],
};

const en: ComparisonContent = {
  eyebrow: "Guide",
  title: "Swedish, international or Spanish school in Spain?",
  metaTitle: "Swedish, international or Spanish school? | Comparison",
  metaDescription:
    "A neutral comparison of Swedish, international and Spanish schools on the Costa del Sol: curriculum, language, moving back, integration and fees.",
  intro:
    "Should your children attend a Swedish, international or Spanish school while you live in Spain? There is no answer that fits every family. This guide compares the three options factually so you can weigh how long you are staying, what happens afterwards and what you want out of these years.",
  neutralityNote:
    "We run a Swedish school, but this comparison is written to be useful even if you choose something else. No option is generally best – and schools within the same category differ, so visit several before you decide.",
  tableHeading: "The comparison in brief",
  tableIntro:
    "The table describes how the three types of school usually work in the province of Málaga. Individual schools can differ on every point.",
  columnLabels: {
    row: "",
    swedish: "Swedish school",
    international: "International school",
    spanish: "Spanish school",
  },
  rows: [
    {
      label: "Curriculum",
      swedish: "Swedish national curriculum and syllabuses",
      international: "British, American or IB – sometimes several in parallel",
      spanish: "Spanish national curriculum (LOMLOE)",
    },
    {
      label: "Language of teaching",
      swedish: "Swedish, with Spanish and English as subjects",
      international: "English, with Spanish as a subject",
      spanish: "Spanish, often with a bilingual English strand in Andalusia",
    },
    {
      label: "Returning to Sweden",
      swedish: "The pupil follows the same syllabus throughout",
      international: "Some catching up, mainly in Swedish and social studies",
      spanish: "More catching up in Swedish, social studies and often maths sequencing",
    },
    {
      label: "Local integration",
      swedish: "Medium to high – Spanish every day, Swedish setting at school",
      international: "Varies – often an international rather than local circle",
      spanish: "High – daily life happens entirely in Spanish",
    },
    {
      label: "Grades and qualifications",
      swedish: "Swedish assessments and grades that work in the Swedish system",
      international: "IGCSE, A-levels, IB or an American high school diploma",
      spanish: "ESO and Bachillerato, with Selectividad for Spanish universities",
    },
    {
      label: "Cost",
      swedish: "Fee-funded independent school",
      international: "Often the highest fees in the area",
      spanish: "Public school free of charge; concertada and privada charge fees",
    },
    {
      label: "Often suits best",
      swedish: "Families staying for a period who may move home again",
      international: "Families moving between countries or aiming for English-language universities",
      spanish: "Families staying long term who want their children fully inside the Spanish system",
    },
    {
      label: "Values and view of pupils",
      swedish: "Varies by school – check how stated values shape everyday practice",
      international: "Varies by school – check how stated values shape everyday practice",
      spanish: "Varies by school – check how stated values shape everyday practice",
    },
  ],
  tableFootnote:
    `The information is general and refers to the ${currentSchoolYearLabel()} school year. Always check directly with each school – provision, language profiles and fees change.`,
  sections: [
    {
      heading: "Swedish school",
      paragraphs: [
        "A Swedish school abroad follows the Swedish curriculum, teaches in Swedish and applies Swedish knowledge requirements. That makes a return to a Swedish class predictable: the pupil has covered the same core content as classmates back home.",
        "The downside is that daily life can become a bubble unless the school actively works with Spanish and local contact. Ask how much Spanish is genuinely used during the week, and what happens after Year 6 or Year 9 at that particular school.",
      ],
      bullets: [
        "Strengths: continuity in Swedish, a clear route home, familiar school culture and teachers who follow every pupil closely.",
        "Consider: ask how the school works with Spanish and the local community, and what the subject range looks like in your child's year group.",
        "A settled transition: a small school makes the move easier – teachers know every pupil from day one, teaching adapts quickly and each child gets more speaking time and support every day, both when starting and when moving back to Sweden.",

      ],
    },
    {
      heading: "International school",
      paragraphs: [
        "International schools teach in English following a British, American or IB model. They suit families who move between countries, because the same system exists in many cities, and they often open a broad route to English-language universities.",
        "Differences between schools are large. Two schools in the same town can differ completely in size, exam model and how many pupils stay for the full journey. When moving back to Sweden, pupils often need to catch up in Swedish and social studies.",
      ],
      bullets: [
        "Strengths: high-level English, internationally recognised qualifications, portability.",
        "Consider: fee levels, support for new language learners and how Swedish is kept alive.",
      ],
    },
    {
      heading: "Spanish school",
      paragraphs: [
        "A Spanish school – public, concertada or private – is the fastest route into local life. Over time children reach native-level Spanish, make local friends and follow the same system as their neighbours.",
        "It does take time before a child without Spanish can follow lessons fully, and support for newcomers varies between schools. If the family plans to move home within a few years, Swedish needs to be maintained alongside, for example through supplementary Swedish tuition.",
      ],
      bullets: [
        "Strengths: the language, the integration, low or no fees in public schools.",
        "Consider: the language run-up, the shape of the school day and how grades translate on return.",
      ],
    },
    {
      heading: "Weighing it up",
      paragraphs: [
        "Three questions usually decide it: How long are you staying? What happens after that period? And how much energy does the family have to support a new language alongside schoolwork?",
        "A common pattern is that families planning one to four years choose a Swedish or international school, while families who see Spain as permanent more often choose a Spanish school. That is a pattern, not a rule – your child's age, language experience and personality matter more than any average.",
      ],
    },
    {
      heading: "Examine the values – not just the words",
      paragraphs: [
        "A school's values shape a child's daily life as much as curriculum, language and fees. Polished wording on a website is not enough. Notice how adults speak to pupils, how conflicts are followed up, how support begins and whether staff genuinely know each child's strengths and needs.",
        "Size alone does not determine quality. A large school can have strong routines and close follow-up, while a small school must also show that its values guide real decisions. Ask whether growth is the main goal, or whether the school can grow without losing sight of each child's wellbeing, development and strong start in life.",
      ],
    },
  ],
  checklistHeading: "Questions to ask on a school visit",
  checklistIntro:
    "The same questions work for every type of school and make it easier to compare like for like.",
  checklist: [
    "How many pupils are in the class, and how many adults are present during the day?",
    "Which language is used in lessons, at break and in communication with parents?",
    "How is a child supported who starts without the language of instruction?",
    "What happens after the highest year group the school offers?",
    "How does the school view a move back home – are there earlier examples?",
    "Which costs come on top of the term fee (lunch, trips, materials, bus)?",
    "Which authority supervises the school, and which permits does it hold?",
    "How does the school work with wellbeing, safety and extra support?",
    "Which values guide the school, and how are they visible in lessons, at break and when something goes wrong?",
    "How does the school maintain personal follow-up as pupil numbers grow?",
  ],
  ownPerspectiveHeading: "Our own perspective",
  ownPerspectiveNote:
    "This section describes our own school. Read it as one of several options – not as a recommendation against the others.",
  ownPerspective: [
    "We teach in Swedish following the Swedish curriculum, with extended Spanish and English every week.",
    "We aim for no more than 16 pupils per group and Swedish-qualified teachers for all Swedish tuition. Group sizes and staffing may vary.",
    "The school is a private school with a permit from and supervision by Junta de Andalucía (the Spanish education authority for Andalusia), and is approved by the Swedish National Agency for Education for supplementary Swedish.",
    "From Year 7 pupils study through Sofia Distans, and sixth form through Hermods, with tutoring on site.",
  ],
  faqHeading: "Frequently asked questions",
  faq: [
    {
      q: "What is the difference between a Swedish and an international school in Spain?",
      a: "A Swedish school teaches in Swedish following the Swedish curriculum; an international school usually teaches in English following a British, American or IB model. The difference shows most in language and when moving back to Sweden.",
    },
    {
      q: "Can my child return to a Swedish school afterwards?",
      a: "Yes. From a Swedish school the transition needs no catching up because the syllabus is the same. From an international or Spanish school it also works, but Swedish and social studies often need to be caught up.",
    },
    {
      q: "Does my child need Spanish to start in a Spanish school?",
      a: "No, but it helps. Younger children usually pick up the language faster than teenagers, and support for newcomers varies a lot between schools – ask specifically how it works.",
    },
    {
      q: "What do the different types of school cost?",
      a: "Public Spanish schools are free, concertada schools charge lower fees, while international and Swedish schools are fee-funded. International schools are often the most expensive in the area.",
    },
    {
      q: "Which option is best?",
      a: "It depends on how long you are staying and what happens next. None of the options is generally better than the others – compare individual schools, not just categories.",
    },
  ],
  ctaTitle: "Would you like to see what a Swedish school day looks like here?",
  ctaText:
    "Read more about the school or send an application and we will get back to you about a place and a start date.",
  ctaLabel: "Apply now",
  ctaTo: "/en/application",
  links: [
    { to: "/en/about-the-school", label: "About the school" },
    { to: "/en/questions-and-answers", label: "Questions and answers" },
    { to: "/en/fees", label: "Fees" },
    { to: "/en/moving-to-spain-with-children", label: "Moving to Spain with children" },
    { to: "/en/schools-in-marbella-and-estepona", label: "Schools in Marbella and Estepona" },
  ],
};

const es: ComparisonContent = {
  eyebrow: "Guía",
  title: "¿Colegio sueco, internacional o español en España?",
  metaTitle: "¿Colegio sueco, internacional o español? | Comparativa",
  metaDescription:
    "Comparativa neutral entre colegio sueco, internacional y español en la Costa del Sol: currículo, idioma, regreso a Suecia, integración, titulación y coste.",
  intro:
    "¿Colegio sueco, internacional o español mientras vivís en España? No hay una respuesta válida para todas las familias. Esta guía compara las tres opciones de forma objetiva para que podáis valorar cuánto tiempo os quedáis, qué pasa después y qué queréis de estos años.",
  neutralityNote:
    "Dirigimos un colegio sueco, pero esta comparativa está escrita para ser útil aunque elijáis otra opción. Ninguna alternativa es mejor en general, y dentro de cada categoría los centros difieren: visitad varios antes de decidir.",
  tableHeading: "La comparativa en breve",
  tableIntro:
    "La tabla describe cómo suelen funcionar los tres tipos de centro en la provincia de Málaga. Cada colegio puede diferir en todos los puntos.",
  columnLabels: {
    row: "",
    swedish: "Colegio sueco",
    international: "Colegio internacional",
    spanish: "Colegio español",
  },
  rows: [
    {
      label: "Currículo",
      swedish: "Currículo sueco y sus programaciones",
      international: "Británico, estadounidense o IB, a veces en paralelo",
      spanish: "Currículo español (LOMLOE)",
    },
    {
      label: "Idioma de enseñanza",
      swedish: "Sueco, con español e inglés como asignaturas",
      international: "Inglés, con español como asignatura",
      spanish: "Español, con frecuencia con línea bilingüe en Andalucía",
    },
    {
      label: "Regreso a Suecia",
      swedish: "El alumno sigue el mismo currículo todo el tiempo",
      international: "Cierta adaptación, sobre todo en sueco y ciencias sociales",
      spanish: "Mayor adaptación en sueco, ciencias sociales y secuencia de matemáticas",
    },
    {
      label: "Integración local",
      swedish: "Media o alta: español cada día y entorno sueco en el centro",
      international: "Variable: círculo más internacional que local",
      spanish: "Alta: la vida diaria transcurre en español",
    },
    {
      label: "Notas y titulación",
      swedish: "Valoraciones y notas suecas válidas en el sistema sueco",
      international: "IGCSE, A-levels, IB o high school diploma estadounidense",
      spanish: "ESO y Bachillerato, con Selectividad para la universidad española",
    },
    {
      label: "Coste",
      swedish: "Centro privado financiado con cuotas",
      international: "A menudo las cuotas más altas de la zona",
      spanish: "Pública gratuita; concertada y privada con cuotas",
    },
    {
      label: "Suele encajar con",
      swedish: "Familias que se quedan un periodo y quizá vuelven a Suecia",
      international: "Familias que se mueven entre países o apuntan a universidades en inglés",
      spanish: "Familias que se quedan a largo plazo y quieren el sistema español completo",
    },
    {
      label: "Valores y visión del alumnado",
      swedish: "Depende del centro: comprobad cómo se viven sus valores cada día",
      international: "Depende del centro: comprobad cómo se viven sus valores cada día",
      spanish: "Depende del centro: comprobad cómo se viven sus valores cada día",
    },
  ],
  tableFootnote:
    `La información es general y se refiere al curso ${currentSchoolYearLabel()}. Consultad siempre directamente con cada colegio: la oferta, el perfil lingüístico y las cuotas cambian.`,
  sections: [
    {
      heading: "Colegio sueco",
      paragraphs: [
        "Un colegio sueco en el extranjero sigue el currículo sueco, enseña en sueco y aplica los criterios suecos. Eso hace previsible la vuelta a una clase en Suecia: el alumno ha visto los mismos contenidos que sus compañeros allí.",
        "El riesgo es que la vida diaria se convierta en una burbuja si el centro no trabaja activamente el español y el contacto local. Preguntad cuánto español se usa realmente durante la semana y qué ocurre después de 6.º o 9.º en ese centro.",
      ],
      bullets: [
        "Puntos fuertes: continuidad en sueco, regreso claro, cultura escolar conocida y un seguimiento cercano de cada alumno.",
        "A tener en cuenta: pregunta cómo se trabaja el español y el contacto local, y qué oferta de asignaturas hay en el curso de tu hijo.",
        "Una transición tranquila: un colegio pequeño facilita la mudanza – los profesores conocen a cada alumno desde el primer día, la enseñanza se adapta rápido y cada niño recibe más atención y tiempo de habla cada día, tanto al empezar como al volver a Suecia.",

      ],
    },
    {
      heading: "Colegio internacional",
      paragraphs: [
        "Los colegios internacionales enseñan en inglés siguiendo el modelo británico, estadounidense o IB. Encajan con familias que se mueven entre países, porque el mismo sistema existe en muchas ciudades, y suelen abrir camino a universidades en inglés.",
        "Las diferencias entre centros son grandes: tamaño, modelo de examen y permanencia del alumnado varían mucho. Al volver a Suecia suele hacer falta recuperar sueco y ciencias sociales.",
      ],
      bullets: [
        "Puntos fuertes: inglés de alto nivel, titulaciones reconocidas y portabilidad.",
        "A tener en cuenta: el nivel de cuotas y el apoyo a quien empieza sin el idioma.",
      ],
    },
    {
      heading: "Colegio español",
      paragraphs: [
        "El colegio español —público, concertado o privado— es la vía más rápida hacia la vida local. Con el tiempo los niños alcanzan un español de nivel nativo, hacen amistades locales y siguen el mismo sistema que sus vecinos.",
        "Aun así, hace falta tiempo hasta que un niño sin español siga las clases por completo, y el apoyo a los recién llegados varía. Si la familia piensa volver en pocos años, conviene mantener el sueco en paralelo.",
      ],
      bullets: [
        "Puntos fuertes: el idioma, la integración y la gratuidad en la escuela pública.",
        "A tener en cuenta: el arranque lingüístico y la convalidación de notas al regresar.",
      ],
    },
    {
      heading: "Cómo decidir",
      paragraphs: [
        "Tres preguntas suelen resolverlo: ¿cuánto tiempo os quedáis?, ¿qué pasa después? y ¿cuánta energía tiene la familia para acompañar un idioma nuevo junto con los deberes?",
        "Un patrón habitual: quienes planean de uno a cuatro años eligen colegio sueco o internacional, y quienes ven España como algo permanente eligen con más frecuencia el colegio español. Es un patrón, no una regla: la edad, la experiencia lingüística y el carácter del niño pesan más.",
      ],
    },
    {
      heading: "Comprobad los valores, no solo las palabras",
      paragraphs: [
        "Los valores de un colegio influyen en la vida diaria del niño tanto como el currículo, el idioma y las cuotas. No bastan unas palabras bonitas en la web. Observad cómo los adultos tratan al alumnado, cómo se resuelven los conflictos, cómo se activa el apoyo y si el personal conoce de verdad las fortalezas y necesidades de cada niño.",
        "El tamaño no determina por sí solo la calidad. Un colegio grande puede ofrecer un seguimiento cercano y buenos métodos, y uno pequeño también debe demostrar que sus valores guían las decisiones. Preguntad si el objetivo principal es sumar alumnos o si el centro puede crecer sin perder de vista el bienestar, el desarrollo y un buen comienzo en la vida de cada niño.",
      ],
    },
  ],
  checklistHeading: "Preguntas para la visita al colegio",
  checklistIntro: "Sirven para cualquier tipo de centro y facilitan comparar en igualdad de condiciones.",
  checklist: [
    "¿Cuántos alumnos hay en clase y cuántos adultos durante la jornada?",
    "¿Qué idioma se usa en clase, en el recreo y en la comunicación con las familias?",
    "¿Cómo se apoya a un niño que empieza sin el idioma de enseñanza?",
    "¿Qué ocurre después del curso más alto que ofrece el centro?",
    "¿Cómo ve el centro el regreso al país de origen? ¿Hay casos anteriores?",
    "¿Qué costes se suman a la cuota (comedor, excursiones, material, transporte)?",
    "¿Qué administración supervisa el centro y qué autorizaciones tiene?",
    "¿Cómo se trabajan el bienestar, la convivencia y el apoyo educativo?",
    "¿Qué valores guían el colegio y cómo se notan en clase, en el recreo y cuando algo sale mal?",
    "¿Cómo mantiene el colegio un seguimiento personal cuando aumenta el número de alumnos?",
  ],
  ownPerspectiveHeading: "Nuestra perspectiva",
  ownPerspectiveNote:
    "Aquí describimos nuestro propio colegio. Leedlo como una opción más, no como una recomendación en contra de las demás.",
  ownPerspective: [
    "Enseñamos en sueco según el currículo sueco, con español e inglés ampliados cada semana.",
    "Aspiramos a un máximo de 16 alumnos por grupo y a que toda la enseñanza en sueco la impartan docentes con titulación sueca. Los grupos y la plantilla pueden variar.",
    "Somos un colegio privado autorizado y supervisado por la Junta de Andalucía (la administración educativa competente en Andalucía), y aprobado por la Agencia Nacional Sueca de Educación para la enseñanza complementaria de sueco.",
    "Desde 7.º el alumnado estudia con Sofia Distans y el bachillerato con Hermods, con acompañamiento presencial.",
  ],
  faqHeading: "Preguntas frecuentes",
  faq: [
    {
      q: "¿Qué diferencia hay entre un colegio sueco y uno internacional en España?",
      a: "El colegio sueco enseña en sueco con el currículo sueco; el internacional suele enseñar en inglés con modelo británico, estadounidense o IB. La diferencia se nota sobre todo en el idioma y al volver a Suecia.",
    },
    {
      q: "¿Puede mi hijo volver después a un colegio sueco?",
      a: "Sí. Desde un colegio sueco la transición no requiere recuperaciones porque el currículo es el mismo. Desde un centro internacional o español también funciona, pero suele hacer falta recuperar sueco y ciencias sociales.",
    },
    {
      q: "¿Hace falta saber español para empezar en un colegio español?",
      a: "No, pero ayuda. Los más pequeños suelen adquirir el idioma más rápido que los adolescentes, y el apoyo a recién llegados varía mucho entre centros.",
    },
    {
      q: "¿Cuánto cuestan los distintos tipos de colegio?",
      a: "La escuela pública española es gratuita, la concertada tiene cuotas menores y los colegios internacionales y suecos se financian con cuotas. Los internacionales suelen ser los más caros de la zona.",
    },
    {
      q: "¿Qué opción es la mejor?",
      a: "Depende de cuánto tiempo os quedéis y de qué pase después. Ninguna opción es mejor en general: comparad centros concretos, no solo categorías.",
    },
  ],
  ctaTitle: "¿Queréis ver cómo es aquí una jornada escolar sueca?",
  ctaText:
    "Leed más sobre el colegio o enviad una solicitud y os contestaremos con la plaza y la fecha de inicio.",
  ctaLabel: "Ir a la solicitud",
  ctaTo: "/es/solicitud",
  links: [
    { to: "/es/preguntas-frecuentes", label: "Preguntas frecuentes" },
    { to: "/es/tarifas", label: "Tarifas" },
  ],
};

export const SCHOOL_COMPARISON: Record<Locale, ComparisonContent> = { sv, en, es };

/** Article + FAQPage-schema för jämförelseguiden. */
export function comparisonJsonLd(content: ComparisonContent, url: string, locale: Locale) {
  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: content.metaTitle,
        description: content.metaDescription,
        inLanguage: locale,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        author: { "@type": "Organization", name: "Swedish International School" },
        publisher: { "@type": "Organization", name: "Swedish International School" },
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: content.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
  ];
}
